import { isValidDate } from "../../../../utils/isValidDate";
import {
  AssetAnswers,
  AssetDocumentArea,
  AssetDocumentZone,
  AssetFinancialHoldingKind,
  AssetsTab,
  ProceedingParticipantType,
  SaveAssets,
} from "../../../api/ClientFileApi";
import { ClientFileQueryKey } from "../../../query/clientFileQueryKeys";
import { InventoryUploadAreaConfig } from "../../components/inventory-upload-area/InventoryUploadArea";
import { AssetAnswerField, AssetDocumentField, AssetsFormValues, FinancialHoldingFormValue } from "./AssetsView.types";

export interface AssetUploadArea extends InventoryUploadAreaConfig<AssetDocumentField> {
  /** Whether the area is shown for these answers; a hidden area neither uploads nor receives moves on save. */
  isShown: (answers: AssetAnswers) => boolean;
}

export const ASSET_UPLOAD_AREAS = {
  [AssetDocumentArea.DomesticRealEstate]: {
    area: AssetDocumentArea.DomesticRealEstate,
    zone: AssetDocumentZone.DomesticRealEstate,
    queryKey: ClientFileQueryKey.AssetInventoryDomesticRealEstateDocuments,
    fieldName: "domesticRealEstateDocuments",
    isShown: (answers) => answers.hasDomesticRealEstate === true,
  },
  [AssetDocumentArea.ForeignRealEstate]: {
    area: AssetDocumentArea.ForeignRealEstate,
    zone: AssetDocumentZone.ForeignRealEstate,
    queryKey: ClientFileQueryKey.AssetInventoryForeignRealEstateDocuments,
    fieldName: "foreignRealEstateDocuments",
    isShown: (answers) => answers.hasForeignRealEstate === true,
  },
  [AssetDocumentArea.BuildingSavingsContracts]: {
    area: AssetDocumentArea.BuildingSavingsContracts,
    zone: AssetDocumentZone.BuildingSavingsContracts,
    queryKey: ClientFileQueryKey.AssetInventoryBuildingSavingsContractDocuments,
    fieldName: "buildingSavingsContractDocuments",
    isShown: (answers) => answers.hasBuildingSavingsContracts === true,
  },
  [AssetDocumentArea.SavingsAccounts]: {
    area: AssetDocumentArea.SavingsAccounts,
    zone: AssetDocumentZone.SavingsAccounts,
    queryKey: ClientFileQueryKey.AssetInventorySavingsAccountDocuments,
    fieldName: "savingsAccountDocuments",
    isShown: (answers) => answers.hasSavingsAccounts === true,
  },
  [AssetDocumentArea.LifeInsurances]: {
    area: AssetDocumentArea.LifeInsurances,
    zone: AssetDocumentZone.LifeInsurances,
    queryKey: ClientFileQueryKey.AssetInventoryLifeInsuranceDocuments,
    fieldName: "lifeInsuranceDocuments",
    isShown: (answers) => answers.hasLifeInsurances === true,
  },
  [AssetDocumentArea.PensionInsurances]: {
    area: AssetDocumentArea.PensionInsurances,
    zone: AssetDocumentZone.PensionInsurances,
    queryKey: ClientFileQueryKey.AssetInventoryPensionInsuranceDocuments,
    fieldName: "pensionInsuranceDocuments",
    isShown: (answers) => answers.hasPensionInsurances === true,
  },
  [AssetDocumentArea.SecuritiesDepots]: {
    area: AssetDocumentArea.SecuritiesDepots,
    zone: AssetDocumentZone.SecuritiesDepots,
    queryKey: ClientFileQueryKey.AssetInventorySecuritiesDepotDocuments,
    fieldName: "securitiesDepotDocuments",
    isShown: (answers) => answers.hasSecuritiesDepots === true,
  },
  [AssetDocumentArea.CashAndBankAssets]: {
    area: AssetDocumentArea.CashAndBankAssets,
    zone: AssetDocumentZone.CashAndBankAssets,
    queryKey: ClientFileQueryKey.AssetInventoryCashAndBankAssetDocuments,
    fieldName: "cashAndBankAssetDocuments",
    isShown: (answers) => answers.hasSafeDeposit === true || answers.hasOtherCashAssets === true,
  },
  [AssetDocumentArea.Vehicles]: {
    area: AssetDocumentArea.Vehicles,
    zone: AssetDocumentZone.Vehicles,
    queryKey: ClientFileQueryKey.AssetInventoryVehicleDocuments,
    fieldName: "vehicleDocuments",
    isShown: (answers) => answers.hasVehicles === true,
  },
  [AssetDocumentArea.BuildingsOnForeignLand]: {
    area: AssetDocumentArea.BuildingsOnForeignLand,
    zone: AssetDocumentZone.BuildingsOnForeignLand,
    queryKey: ClientFileQueryKey.AssetInventoryBuildingsOnForeignLandDocuments,
    fieldName: "buildingsOnForeignLandDocuments",
    isShown: (answers) => answers.hasBuildingsOnForeignLand === true,
  },
} satisfies Partial<Record<AssetDocumentArea, AssetUploadArea>>;

export type AssetsDocumentArea = keyof typeof ASSET_UPLOAD_AREAS;

export type FinancialHoldingTranslationKey =
  "buildingSavingsContracts" | "savingsAccounts" | "lifeInsurances" | "pensionInsurances" | "securitiesDepots";

export interface FinancialHoldingKindConfig {
  kind: AssetFinancialHoldingKind;
  translationKey: FinancialHoldingTranslationKey;
  entryField: AssetAnswerField;
  uploadArea: AssetsDocumentArea;
  /** The participant type offered under "zur Auswahl". */
  participantType: ProceedingParticipantType | null;
}

export const FINANCIAL_HOLDING_KINDS: readonly FinancialHoldingKindConfig[] = [
  {
    kind: AssetFinancialHoldingKind.BuildingSavingsContract,
    translationKey: "buildingSavingsContracts",
    entryField: "hasBuildingSavingsContracts",
    uploadArea: AssetDocumentArea.BuildingSavingsContracts,
    participantType: ProceedingParticipantType.Bank,
  },
  {
    kind: AssetFinancialHoldingKind.SavingsAccount,
    translationKey: "savingsAccounts",
    entryField: "hasSavingsAccounts",
    uploadArea: AssetDocumentArea.SavingsAccounts,
    participantType: ProceedingParticipantType.Bank,
  },
  {
    kind: AssetFinancialHoldingKind.LifeInsurance,
    translationKey: "lifeInsurances",
    entryField: "hasLifeInsurances",
    uploadArea: AssetDocumentArea.LifeInsurances,
    participantType: ProceedingParticipantType.Insurance,
  },
  {
    kind: AssetFinancialHoldingKind.PensionInsurance,
    translationKey: "pensionInsurances",
    entryField: "hasPensionInsurances",
    uploadArea: AssetDocumentArea.PensionInsurances,
    participantType: ProceedingParticipantType.Insurance,
  },
  {
    kind: AssetFinancialHoldingKind.SecuritiesDepot,
    translationKey: "securitiesDepots",
    entryField: "hasSecuritiesDepots",
    uploadArea: AssetDocumentArea.SecuritiesDepots,
    participantType: ProceedingParticipantType.Bank,
  },
];

export function createEmptyHolding(key: string, kind: AssetFinancialHoldingKind): FinancialHoldingFormValue {
  return {
    key,
    id: null,
    kind,
    institutionName: null,
    accountNumber: null,
    street: null,
    houseNumber: null,
    zipCode: null,
    city: null,
    country: null,
    encumbrance: null,
    encumberedClaimId: null,
    usedForProceedingCosts: null,
    isValueKnown: null,
    value: null,
    valueDate: null,
    remarks: null,
  };
}

export function holdingIndexesOfKind(holdings: FinancialHoldingFormValue[], kind: AssetFinancialHoldingKind): number[] {
  return holdings.flatMap((holding, index) => (holding.kind === kind ? [index] : []));
}

const EMPTY_ANSWERS: AssetAnswers = {
  hasDomesticRealEstate: null,
  domesticRealEstateDetails: null,
  hasForeignRealEstate: null,
  foreignRealEstateDetails: null,
  hasBuildingSavingsContracts: null,
  hasSavingsAccounts: null,
  hasLifeInsurances: null,
  hasPensionInsurances: null,
  hasSecuritiesDepots: null,
  hasSafeDeposit: null,
  safeDepositDetails: null,
  hasOtherCashAssets: null,
  isSavedForProceeding: null,
  savedForProceedingAmount: null,
  savedForProceedingDate: null,
  otherCashAssetsDetails: null,
  hasValuables: null,
  valuablesDetails: null,
  hasFurnishings: null,
  furnishingsDetails: null,
  hasAppliances: null,
  appliancesDetails: null,
  hasEquipment: null,
  equipmentDetails: null,
  hasVehicles: null,
  vehiclesDetails: null,
  hasBuildingsOnForeignLand: null,
  buildingsOnForeignLandDetails: null,
  hasOtherItems: null,
  otherItemsDetails: null,
  ownsAllItems: null,
  itemsOwnedByOthersDetails: null,
};

const EMPTY_DOCUMENTS = Object.fromEntries(
  Object.values(ASSET_UPLOAD_AREAS).map(({ fieldName }) => [fieldName, [] as File[]])
) as Record<AssetDocumentField, File[]>;

export function withEntryAnswered(
  holdings: FinancialHoldingFormValue[],
  kind: AssetFinancialHoldingKind,
  answer: boolean | null
): FinancialHoldingFormValue[] {
  if (answer !== true) {
    return holdings.filter((holding) => holding.kind !== kind);
  }
  if (holdingIndexesOfKind(holdings, kind).length > 0) {
    return holdings;
  }
  return [...holdings, createEmptyHolding(crypto.randomUUID(), kind)];
}

export function withAddedHolding(
  holdings: FinancialHoldingFormValue[],
  kind: AssetFinancialHoldingKind
): FinancialHoldingFormValue[] {
  const kindIndexes = holdingIndexesOfKind(holdings, kind);
  const insertAt = (kindIndexes[kindIndexes.length - 1] ?? holdings.length - 1) + 1;
  return [...holdings.slice(0, insertAt), createEmptyHolding(crypto.randomUUID(), kind), ...holdings.slice(insertAt)];
}

export function withoutHolding(holdings: FinancialHoldingFormValue[], index: number): FinancialHoldingFormValue[] {
  const removed = holdings[index];
  if (!removed) {
    return holdings;
  }
  const remaining = holdings.filter((_, i) => i !== index);
  if (holdingIndexesOfKind(remaining, removed.kind).length > 0) {
    return remaining;
  }
  return [...remaining, createEmptyHolding(crypto.randomUUID(), removed.kind)];
}

export function fromAssetsResponse(data: AssetsTab | undefined): AssetsFormValues {
  return {
    answers: data?.answers ?? EMPTY_ANSWERS,
    financialHoldings: (data?.financialHoldings ?? []).map((holding) => ({
      key: holding.id,
      id: holding.id,
      kind: holding.kind,
      institutionName: holding.institutionName ?? null,
      accountNumber: holding.accountNumber ?? null,
      street: holding.street ?? null,
      houseNumber: holding.houseNumber ?? null,
      zipCode: holding.zipCode ?? null,
      city: holding.city ?? null,
      country: holding.country ?? null,
      encumbrance: holding.encumbrance ?? null,
      encumberedClaimId: holding.encumberedClaimId ?? null,
      usedForProceedingCosts: holding.usedForProceedingCosts ?? null,
      isValueKnown: holding.isValueKnown ?? null,
      value: holding.value ?? null,
      valueDate: holding.valueDate ?? null,
      remarks: holding.remarks ?? null,
    })),
    ...EMPTY_DOCUMENTS,
    documentMoves: [],
  };
}

export function toAssetsRequest(values: AssetsFormValues): SaveAssets {
  const { answers } = values;
  return {
    answers: {
      ...answers,
      savedForProceedingDate: isValidDate(answers.savedForProceedingDate) ? answers.savedForProceedingDate : null,
    },
    financialHoldings: values.financialHoldings.map(({ key: _key, valueDate, ...holding }) => ({
      ...holding,
      valueDate: isValidDate(valueDate) ? valueDate : null,
    })),
  };
}
